import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Formik, Form, Field } from 'formik'
import _get from 'lodash/get'
import _map from 'lodash/map'
import _find from 'lodash/find'
import _forEach from 'lodash/forEach'
import _values from 'lodash/values'
import _filter from 'lodash/filter'
import _upperFirst from 'lodash/upperFirst'
import SeatMapView, { SEAT_STATUSES } from 'tf-seat-map-view'
import { SimpleField, SelectField } from '../../formik/Fields'
import Button from '../../_library/Button'
import SortableTable from '../../_library/SortableTable'
import { showAxiosError, showSuccessMessage } from '../../utils/messenger'
import Card from '../../_library/Card'
import { getSeatMapData, getSeatMapStatuses } from '../../../_common/core/http_services/seatmap'
import LoadingBar from '../../_library/LoadingBar'
import { getTableColumns } from '../../utils/sortableTableUtils'
import {
  createPredefinedInvitation,
  deletePredefinedInvitation,
  getPredefinedInvitations,
  resendPredefinedInvitation,
  getPromoters,
} from '../../../_common/core/http_services'
import { combineValidators, isEmailValidator, requiredValidator } from '../../../_common/core/validation'
import {
  createChannelSeatsIo,
  editChannelSeatsIo,
  fetchSeatsIoData,
  fetchSeatsIoEvent,
  fetchSeatsIoInvitations,
  fetchSeatsIoInvitationUrl,
  ordersSeatsIoInvitation,
  removeChannelSeatsIo,
  resendSeatsIoInvitation,
  sendSeatsIoInvitations,
  withdrawSeatsIoInvitation,
} from '../../../_common/core/http_services/seatsio'
import { SeatsioEventManager } from '@seatsio/seatsio-react'
import ConfirmModal from '../../_library/ConfirmModal'
import modalStyle from '../../../_common/core/modalStyle'
import ReactModal from 'react-modal'
import SortableTableWithPaginationHOC from '../../hoc/SortableTableWithPaginationHOC'
import EmptyBar from '../../_library/EmptyBar'
import PermissionDenied from '../../_library/PermissionDeniedComponent'
import { connect } from 'react-redux'
import { get_event } from '../../../_common/core/selectors'

const SortableTableWithPagination = SortableTableWithPaginationHOC()
const DEFAULT_PAGE_SIZE = 10

const PredefinedInvitationsContainer = props => {
  const isSeatMapAllowed = props.event?.flagSeatMapAllowed && props.event?.seatProvider !== 'seats_io'
  const eventId = _get(props, 'match.params.id')
  const seatMapWidth = window.innerWidth < 450 ? window.innerWidth - 66 : Math.min(window.innerWidth / 2, 600)
  const [selectedSeats, setSelectedSeats] = useState([])
  const [seatMapData, setSeatMapData] = useState(null)
  const [ticketTypeTireRelationsArray, setTicketTypeTireRelationsArray] = useState(null)
  const [promotersData, setPromotersData] = useState(null)
  const [predefinedData, setPredefinedData] = useState([])
  const [detailRows, setDetailRows] = useState([])
  const [seatsStatuses, setSeatsStatuses] = useState(null)
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true)
  const [isDeletingInvitationData, setIsDeletingInvitationData] = useState({})
  const [isResendingInvitation, setIsResendingInvitation] = useState({})
  const [isFetchingInvitationUrl, setIsFetchingInvitationUrl] = useState({})

  useEffect(() => {
    // Fetch seat-map-data
    const fetchSeatMapData = async () => {
      try {
        const response = await getSeatMapData(eventId)

        const ticketTypeTireRelationsArray = []
        _forEach(response.ticketTypeTierRelations, (item, key) => {
          ticketTypeTireRelationsArray.push(..._values(item))
        })

        setTicketTypeTireRelationsArray(ticketTypeTireRelationsArray)
        setSeatMapData(response)
      } catch (error) {
        showAxiosError(error)
      }
    }

    // Fetch Promoters data
    const fetchPromotersData = async () => {
      try {
        const response = await getPromoters(eventId)
        setPromotersData(
          _map(
            _filter(response.data, promoter => !promoter.email.includes('@theticketfairy.com')),
            item => ({
              ...item,
              fullName: `${item.firstName} ${item.lastName} (${item.email})`,
            }),
          ),
        )
      } catch (error) {
        showAxiosError(error)
      }
    }

    // Fetch seats statuses
    const fetchSeatMapStatuses = async () => {
      try {
        const response = await getSeatMapStatuses(eventId)
        setSeatsStatuses(response)
      } catch (error) {
        showAxiosError(error)
      } finally {
        setIsLoadingStatuses(false)
      }
    }

    if (isSeatMapAllowed) {
      fetchSeatMapData()
      fetchPromotersData()
      fetchSeatMapStatuses()
    }

    const statusesInterval = setInterval(() => {
      if (isSeatMapAllowed) {
        fetchSeatMapStatuses()
      }
    }, 5000)

    return () => {
      clearInterval(statusesInterval)
    }
  }, [eventId, isSeatMapAllowed])

  // Fetch predefined table’s data
  const fetchPredefinedData = useCallback(async () => {
    if (isSeatMapAllowed) {
      try {
        const response = await getPredefinedInvitations(eventId)
        setPredefinedData(
          _map(response, item => ({
            ...item,
            statusText: _upperFirst(item.status || ''),
            enableDelete: item.can_delete_invitation,
            enableResend: item.can_resend_invitation,
            seatTicket: _map(item.seats, seatItem => seatItem.name).join(', '),
            recipientEmail: item.email,
            promoterEmail: _find(promotersData, promoterItem => promoterItem.promoterId === item.promoterId)
              ?.email,
          })),
        )

        const detailRows = _map(response, item => {
          const data = _map(item.seats, (seatItem, index) => ({
            id: item.id,
            statusText: _upperFirst(seatItem.status || ''),
            name: seatItem.name,
            seatId: seatItem.id,
            enableDelete: seatItem.can_delete_invitation,
          }))

          return {
            id: item.id,
            type: 'detailRow',
            component: (
              <SortableTable
                e2e_test_id={`predefined-${item.id}`}
                data={data}
                tableColumns={getTableColumns([
                  { key: 'name', label: 'Seat Ticket' },
                  { key: 'statusText', label: 'Status' },
                ])}
                enableSort={true}
                enableCopyTable={false}
                disableMobileView={true}
                className="child-table"
                actions={[
                  {
                    label: 'Delete',
                    className: 'btn btn-primary btn-danger',
                    disabled: rowData => !rowData.enableDelete,
                    onClick: rowData => deleteRecipient(rowData),
                  },
                ]}
              />
            ),
          }
        })
        setDetailRows(detailRows)
      } catch (error) {
        showAxiosError(error)
      }
    }
  }, [isSeatMapAllowed, eventId, promotersData])

  useEffect(() => {
    if (promotersData && isSeatMapAllowed) {
      fetchPredefinedData()
    }
  }, [fetchPredefinedData, promotersData, isSeatMapAllowed])

  const handleFormSubmit = async (values, formikActions) => {
    try {
      const response = await createPredefinedInvitation(eventId, {
        email: values.recipientEmail,
        tierId: selectedSeats[0].tierId,
        promoter_id: values.promoterId,
        seats: _map(selectedSeats, item => item.seatId),
      })
      showSuccessMessage(response.message)
      setSelectedSeats([])
      fetchPredefinedData()
    } catch (error) {
      showAxiosError(error)
    } finally {
      formikActions.setSubmitting(false)
      formikActions.resetForm()
    }
  }

  const deleteRecipient = async rowData => {
    try {
      setIsDeletingInvitationData({ [rowData.id]: true })
      const response = await deletePredefinedInvitation(eventId, rowData.id, rowData.seatId)
      showSuccessMessage(response.message)
      fetchPredefinedData()
    } catch (error) {
      showAxiosError(error)
    } finally {
      setIsDeletingInvitationData({ [rowData.id]: false })
    }
  }

  const resendInvitation = async rowData => {
    try {
      setIsResendingInvitation({ [rowData.id]: true })
      const response = await resendPredefinedInvitation(eventId, rowData.id)
      showSuccessMessage(response.message)
    } catch (error) {
      showAxiosError(error)
    } finally {
      setIsResendingInvitation({ [rowData.id]: false })
    }
  }

  const copyInvitation = async rowData => {
    try {
      setIsFetchingInvitationUrl(prev => ({ ...prev, [rowData.id]: true }))
      const response = await fetchPredefinedInvitationUrl(eventId, rowData.id)
      const invitationUrl = response.data.attributes.url
      await navigator.clipboard.writeText(invitationUrl)
      showSuccessMessage('Invitation URL copied to clipboard')
      setIsFetchingInvitationUrl({ [rowData.id]: false })
    } catch (error) {
      showAxiosError({ message: 'Failed to copy invitation URL' })
    } finally {
      setIsFetchingInvitationUrl({ [rowData.id]: false })
    }
  }

  if (!isSeatMapAllowed) {
    return <PermissionDenied subHeader="You don't have access to this section." />
  }

  return (
    <div className="predefined-invitations">
      <Card title={'Send Predefined Invitations'}>
        {!isLoadingStatuses && Boolean(seatMapData) ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40 }}>
            <SeatMapView
              disabled={isLoadingStatuses}
              events={{
                onSeatClick: e => {
                  const { seatId } = e.seat
                  if (e.seat.status === SEAT_STATUSES.AVAILABLE) {
                    const isSeatSelected = _find(selectedSeats, item => item.seatId === seatId)
                    if (isSeatSelected) {
                      e.seat.unselect(e.seat.label)
                      setSelectedSeats(_filter(selectedSeats, item => item.seatId !== seatId))
                    } else {
                      e.seat.select(e.seat.label)
                      selectedSeats.push(e.seat)
                      setSelectedSeats([...selectedSeats])
                    }
                  }
                },
              }}
              loading={isLoadingStatuses}
              seatData={JSON.parse(seatMapData.seatData)}
              statuses={seatsStatuses || {}}
              width={seatMapWidth}
              height={seatMapWidth}
              isSelectionOn={true}
              ticketTypeTireRelationsArray={ticketTypeTireRelationsArray}
              isTableMap={true}
            />
            <div style={{ width: '100%', minWidth: '280px', maxWidth: 500 }}>
              <Formik
                initialValues={{ recipientEmail: '', promoterId: '' }}
                onSubmit={(values, formikActions) => handleFormSubmit(values, formikActions)}
              >
                {({ isSubmitting, isValid }) => (
                  <Form>
                    <Field
                      name="recipientEmail"
                      component={SimpleField}
                      type="email"
                      placeholder="Recipient Email"
                      validate={combineValidators(requiredValidator(), isEmailValidator())}
                    />
                    <Field
                      name="promoterId"
                      component={SelectField}
                      options={[
                        {
                          label: 'Select Promoter (Optional)',
                          value: '',
                        },
                        ..._map(promotersData, item => ({
                          label: item.fullName,
                          value: item.promoterId,
                        })),
                      ]}
                      placeholder="Promoter Email"
                    />
                    <div className="text-center">
                      <Button
                        type="submit"
                        disabled={!Boolean(selectedSeats?.length) || !isValid}
                        loading={isSubmitting}
                        className="btn btn-primary btn-success"
                      >
                        Send
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        ) : (
          <LoadingBar />
        )}
      </Card>
      <Card title="Recipients">
        <SortableTable
          data={predefinedData}
          tableColumns={getTableColumns([
            { key: 'seatTicket', label: 'Seat Ticket' },
            { key: 'recipientEmail', label: 'Recipient Email' },
            { key: 'promoterEmail', label: 'Promoter Email', normalizer: value => value || 'No Promoter' },
            { key: 'statusText', label: 'Status' },
          ])}
          actions={[
            {
              label: 'Resend',
              className: 'btn btn-primary',
              icon: (
                <img
                  alt="nodata"
                  className="default-icosize"
                  src={asset('/resources/images/resend-ico.svg')}
                />
              ),
              disabled: rowData => !rowData.enableResend,
              isLoading: rowData => isResendingInvitation[rowData.id],
              onClick: rowData => resendInvitation(rowData),
            },
            {
              label: 'Copy',
              className: 'btn btn-primary btn-secondary',
              icon: (
                <img
                  alt="copy"
                  className="default-icosize"
                  src={asset('/resources/images/content-duplicate.svg')}
                />
              ),
              isLoading: rowData => isFetchingInvitationUrl[rowData.id],
              onClick: rowData => copyInvitation(rowData),
            },
            {
              label: 'Delete',
              className: 'btn btn-primary btn-danger',
              disabled: rowData => !rowData.enableDelete,
              isLoading: rowData => isDeletingInvitationData[rowData.id],
              onClick: rowData => deleteRecipient(rowData),
            },
          ]}
          disableMobileView={true}
          enableSort={true}
          detailsRows={detailRows}
          enableCopyTable={true}
        />
      </Card>
    </div>
  )
}

const PredefinedInvitationSeatsIo = props => {
  const eventId = _get(props, 'match.params.id')
  const [promotersData, setPromotersData] = useState(null)
  const [predefinedData, setPredefinedData] = useState([])
  const [seatsIoData, setSeatsIoData] = useState(null)
  const [configuration, setConfiguration] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
  })
  const [isLoadingPage, setIsLoadingPage] = useState(true)
  const [total, setTotal] = useState(0)
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(true)

  const fetchPromotersData = async () => {
    try {
      const response = await getPromoters(eventId)
      setPromotersData(
        _map(
          _filter(response.data, promoter => !promoter.email.includes('@theticketfairy.com')),
          item => ({
            ...item,
            fullName: `${item.firstName} ${item.lastName} (${item.email})`,
          }),
        ),
      )
    } catch { }
  }

  const fetchSeatsIoEventData = async () => {
    try {
      const response = await fetchSeatsIoEvent(eventId)
      setSeatsIoData(response)
    } catch { }
  }

  const fetchConfiguration = async () => {
    try {
      setIsLoadingConfiguration(true)
      const response = await fetchSeatsIoData(eventId)
      setConfiguration(response)
    } catch (error) {
      showAxiosError(error)
    } finally {
      setIsLoadingConfiguration(false)
    }
  }

  const fetchPredefinedData = async (page = 1, limit = DEFAULT_PAGE_SIZE) => {
    try {
      setIsLoadingPage(true)
      const response = await fetchSeatsIoInvitations(eventId, limit, page)
      if (page === 1) {
        setTotal(response.pagination.total)
      }
      setPredefinedData(response)
    } catch {
    } finally {
      setIsLoadingPage(false)
    }
  }

  useEffect(() => {
    fetchPromotersData()
    fetchSeatsIoEventData()
    fetchConfiguration()
    fetchPredefinedData()
  }, [])

  const [isLoadingEditId, setIsLoadingEditId] = useState("")
  const [isLoadingDeleteId, setIsLoadingDeleteId] = useState("")
  const [showModalDeleteId, setShowModalDeleteId] = useState("")
  const [showModalEditId, setShowModalEditId] = useState({
    id: "",
    name: "",
    color: "",
    channelId: "",
  })
  const [showModalResendInvitationId, setShowModalResendInvitationId] = useState("")
  const [isLoadingResendInvitationId, setIsLoadingResendInvitationId] = useState("")
  const [showModalWithdrawInvitationId, setShowModalWithdrawInvitationId] = useState("")
  const [isLoadingWithdrawInvitationId, setIsLoadingWithdrawInvitationId] = useState("")
  const [isFetchingInvitationUrl, setIsFetchingInvitationUrl] = useState({})

  const chartRef = useRef(false)

  const refetchData = (rerender = true) => {
    fetchPredefinedData(pagination.page, pagination.limit)
    fetchSeatsIoEventData()
    if (rerender) {
      chartRef.current?.chart?.rerender()
    }
  }

  const onConfirmDelete = async option => {
    if (option === "ok") {
      try {
        setIsLoadingDeleteId(showModalDeleteId)
        await removeChannelSeatsIo({ eventId, channelId: showModalDeleteId })
        refetchData()
        showSuccessMessage("Channel deleted successfully!")
      } catch (error) {
        showAxiosError(error)
      } finally {
        setIsLoadingDeleteId("")
        setShowModalDeleteId("")
      }
    } else {
      setShowModalDeleteId("")
    }
  }

  const onConfirmResend = async option => {
    if (option === "ok") {
      try {
        setIsLoadingResendInvitationId(showModalResendInvitationId)
        await resendSeatsIoInvitation({ eventId, invitationId: showModalResendInvitationId })
        refetchData()
        showSuccessMessage("Invitation sent successfully!")
      } catch (error) {
        showAxiosError(error)
      } finally {
        setIsLoadingResendInvitationId("")
        setShowModalResendInvitationId("")
      }
    } else {
      setShowModalResendInvitationId("")
      setIsLoadingResendInvitationId("")
    }
  }

  const onConfirmWithdraw = async option => {
    if (option === "ok") {
      try {
        setIsLoadingWithdrawInvitationId(showModalWithdrawInvitationId)
        await withdrawSeatsIoInvitation({ eventId, invitationId: showModalWithdrawInvitationId })
        refetchData()
        showSuccessMessage("Invitation was withdrawn successfully!")
      } catch (error) {
        showAxiosError(error)
      } finally {
        setIsLoadingWithdrawInvitationId("")
        setShowModalWithdrawInvitationId("")
      }
    } else {
      setIsLoadingWithdrawInvitationId("")
      setShowModalWithdrawInvitationId("")
    }
  }

  const onConfirmEdit = async ({ eventId, name, color, channelId }) => {
    try {
      setIsLoadingEditId(channelId)
      await editChannelSeatsIo({
        eventId,
        name,
        color,
        channelId,
      })
      refetchData()
      showSuccessMessage("Channel updated successfully!")
    } catch (error) {
      showAxiosError(error)
    } finally {
      setIsLoadingEditId("")
      setShowModalEditId({
        id: "",
        name: "",
        color: "",
        channelId: "",
      })
    }
  }

  const predefinedDataTable = _map(predefinedData?.invitations ?? [], item => ({
    ...item,
    statusText: _upperFirst(item.status || ''),
    recipientEmail: item.email,
  }))

  const [detailRowData, setDetailRowData] = useState(null)

  const getDetailRowData = async data => {
    try {
      const customerData = await ordersSeatsIoInvitation({ eventId, invitationId: data.id })
      customerData.invitationId = data.id
      setDetailRowData(customerData)
    } catch (e) {
      showAxiosError(e)
    }
  }

  const detailRowTableData = (detailRowData?.orders ?? []).map(item => ({
    id: item.id,
    statusText: _upperFirst(item.status || ''),
    name: item.seats,
  }))

  const detailRows = [{
    id: detailRowData?.invitationId,
    type: 'detailRow',
    component: (
      <SortableTable
        e2e_test_id={`predefined-${detailRowData?.invitationId}`}
        data={detailRowTableData}
        tableColumns={getTableColumns([
          { key: 'name', label: 'Seat Ticket' },
          { key: 'statusText', label: 'Status' },
        ])}
        enableSort={true}
        enableCopyTable={false}
        disableMobileView={true}
        className="child-table"
      />
    ),
  }]

  const copyInvitation = async rowData => {
    try {
      setIsFetchingInvitationUrl({ [rowData.id]: true })
      const response = await fetchSeatsIoInvitationUrl(eventId, rowData.id)
      const invitationUrl = response.data.attributes.url
      await navigator.clipboard.writeText(invitationUrl)
      showSuccessMessage('Invitation URL copied to clipboard')
      setIsFetchingInvitationUrl({ [rowData.id]: false })
    } catch (error) {
      showAxiosError({ message: 'Failed to copy invitation URL' })
      setIsFetchingInvitationUrl({ [rowData.id]: false })
    }
  }

  const onPageOrLimitChange = (pageNumber, pageSize) => {
    if (pageSize !== pagination.limit) {
      setPagination({ page: 1, limit: pageSize })
      fetchPredefinedData(1, pageSize)
    } else {
      setPagination({ page: pageNumber, limit: pageSize })
      fetchPredefinedData(pageNumber, pageSize)
    }
  }

  return (
    <div className="predefined-invitations">
      <ConfirmModal
        isOpen={!!showModalWithdrawInvitationId}
        header={'Withdraw Invitation?'}
        content={'Are you sure you want to withdraw this invitation?'}
        actions={[
          { value: 'ok', label: 'Ok', className: 'btn btn-success btn-shadow' },
          { value: 'cancel', label: 'Cancel', className: 'btn btn-default' },
        ]}
        onAction={onConfirmWithdraw}
      />
      <ConfirmModal
        isOpen={!!showModalResendInvitationId}
        header={'Resend Invitation?'}
        content={'Are you sure you want to resend this invitation?'}
        actions={[
          { value: 'ok', label: 'Ok', className: 'btn btn-success btn-shadow' },
          { value: 'cancel', label: 'Cancel', className: 'btn btn-default' },
        ]}
        onAction={onConfirmResend}
      />
      <ConfirmModal
        isOpen={!!showModalDeleteId}
        header={'Confirm Delete'}
        content={'Are you sure you want to delete this channel?'}
        actions={[
          { value: 'ok', label: 'Ok', className: 'btn btn-success btn-shadow' },
          { value: 'cancel', label: 'Cancel', className: 'btn btn-default' },
        ]}
        onAction={onConfirmDelete}
      />
      <ReactModal
        className="timeslot-edit-modal modal-dialog modal-trans"
        style={{
          overlay: modalStyle.overlay,
          content: { ...modalStyle.content, overflow: 'visible' },
        }}
        isOpen={!!showModalEditId?.id}
        contentLabel="Modal"
        onRequestClose={() => {
          setShowModalEditId({
            id: "",
            name: "",
            color: "",
            channelId: "",
          })
        }}
        closeTimeoutMS={150}
        ariaHideApp={false}
      >
        <div className="modal-dialog" style={{ overflow: 'visible' }}>
          <div className="modal-content">
            <div className="modal-header">
              <div>Edit Channel</div>
            </div>
            <div className="card-space">
              <div className="card-block">
                <Formik
                  initialValues={{ channelName: showModalEditId.name, channelColor: showModalEditId.color }}
                  onSubmit={async values => onConfirmEdit({
                    eventId,
                    color: values.channelColor,
                    name: values.channelName,
                    channelId: showModalEditId.id,
                  })}
                >
                  {({ isSubmitting, isValid }) => (
                    <Form>
                      <Field
                        label="Name"
                        name="channelName"
                        component={SimpleField}
                        type="text"
                        placeholder="Name"
                        validate={requiredValidator()}
                      />
                      <Field
                        label="Color"
                        type="color"
                        name="channelColor"
                        component={SimpleField}
                        validate={requiredValidator()}
                      />
                      <div className="text-center btn-toolbar">
                        <Button
                          type="button"
                          disabled={!isValid}
                          loading={isSubmitting}
                          className="btn btn-cancel"
                          onClick={() => {
                            setShowModalEditId({
                              id: "",
                              name: "",
                              color: "",
                              channelId: "",
                            })
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={!isValid}
                          loading={isSubmitting}
                          className="btn btn-primary btn-success"
                        >
                          Edit Channel
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </ReactModal>
      <Card title={'Create and Edit Channels'}>
        {isLoadingConfiguration ? (
          <LoadingBar />
        ) : configuration ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40, justifyContent: "center" }}>
            <div style={{ width: '100%', minWidth: '280px', maxWidth: 500 }}>
              <Formik
                initialValues={{ channelName: '', channelColor: '#000000' }}
                onSubmit={async (values, formikActions) => {
                  try {
                    await createChannelSeatsIo({ eventId, color: values.channelColor, name: values.channelName })
                    refetchData()
                    formikActions.resetForm()
                    showSuccessMessage("Channel created successfully!")
                  } catch (e) {
                    showAxiosError(e)
                  }
                }}
              >
                {({ isSubmitting, isValid }) => (
                  <Form>
                    <Field
                      label="Name"
                      name="channelName"
                      component={SimpleField}
                      type="text"
                      placeholder="Name"
                      validate={requiredValidator()}
                    />
                    <Field
                      label="Color"
                      type="color"
                      name="channelColor"
                      component={SimpleField}
                      validate={requiredValidator()}
                    />
                    <div className="text-center" style={{ marginBottom: 15 }}>
                      <Button
                        type="submit"
                        disabled={!isValid}
                        loading={isSubmitting}
                        className="btn btn-primary btn-success"
                      >
                        Create Channel
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        ) : (
          <EmptyBar />
        )}
        {seatsIoData ? <SortableTable
          data={seatsIoData?.channels?.map(channel => ({
            id: channel.id,
            name: channel.name,
            color: channel.color,
          })) ?? []}
          tableColumns={getTableColumns([
            {
              key: 'id',
              label: 'ID',
            },
            {
              key: 'name',
              label: 'Name',
            },
            {
              key: 'color',
              label: 'Color',
              normalizer: value => <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ display: "block", background: value, height: 20, width: 20, borderRadius: 10 }} />
                <span>{value}</span>
              </div>,
            },
          ])}
          actions={[
            {
              label: 'Edit',
              className: 'btn btn-primary',
              icon: (<i className="fa fa-pencil fa-fw" />),
              isLoading: rowData => isLoadingEditId.id === rowData.id,
              onClick: ({ id, name, color }) => setShowModalEditId({ id, name, color }),
            },
            {
              label: 'Delete',
              className: 'btn btn-primary btn-danger',
              icon: (<i className="fa fa-trash fa-fw" />),
              isLoading: rowData => isLoadingDeleteId === rowData.id,
              onClick: rowData => setShowModalDeleteId(rowData.id),
            },
          ]}
          disableMobileView={true}
          enableSort={true}
          enableCopyTable={false}
        /> : null}
      </Card>
      <Card title={'Assigning Seats to Channels'}>
        <div><strong>Warning</strong>: Attaching a General Admission Zone to a channel will attach all maximun spaces</div>
        {isLoadingConfiguration ? (
          <LoadingBar />
        ) : configuration ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40, justifyContent: 'center' }}>
            <SeatsioEventManager
              ref={chartRef}
              secretKey={configuration.workplace_secret_key}
              event={configuration.event_id}
              mode="manageChannels"
              region={configuration.region}
              manageChannelsList={false}
              onSubmitSucceeded={() => {
                refetchData(false)
              }}
            />
          </div>
        ) : (
          <EmptyBar />
        )}
      </Card>
      <Card title={'Send Channel as Predefined Invitation'}>
        {isLoadingConfiguration ? (
          <LoadingBar />
        ) : configuration ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40, justifyContent: 'center' }}>
            <div style={{ width: '100%', minWidth: '280px', maxWidth: 500 }}>
              <Formik
                initialValues={{ recipientEmail: '', promoterId: '', channelId: '' }}
                onSubmit={async (values, formikActions) => {
                  try {
                    await sendSeatsIoInvitations({
                      eventId,
                      email: values.recipientEmail,
                      seatsIoChannelId: values.channelId,
                      promoterId: values.promoterId,
                    })
                    refetchData()
                    showSuccessMessage('Invitation sent successfully!')
                  } catch (error) {
                    showAxiosError(error)
                  } finally {
                    formikActions.setSubmitting(false)
                    formikActions.resetForm()
                  }
                }}
              >
                {({ isSubmitting, isValid }) => (
                  <Form>
                    <Field
                      name="recipientEmail"
                      component={SimpleField}
                      type="email"
                      placeholder="Recipient Email"
                      validate={combineValidators(requiredValidator(), isEmailValidator())}
                    />
                    <Field
                      name="channelId"
                      component={SelectField}
                      options={[
                        {
                          label: 'Select Channel',
                          value: '',
                        },
                        ..._map(seatsIoData?.channels?.filter(channel => !!channel?.objects?.length), item => ({
                          label: item.name,
                          value: item.id,
                        })),
                      ]}
                      placeholder="Channel ID"
                    />
                    <Field
                      name="promoterId"
                      component={SelectField}
                      options={[
                        {
                          label: 'Select Promoter (Optional)',
                          value: '',
                        },
                        ..._map(promotersData, item => ({
                          label: item.fullName,
                          value: item.promoterId,
                        })),
                      ]}
                      placeholder="Promoter Email"
                    />
                    <div className="text-center">
                      <Button
                        type="submit"
                        disabled={!isValid}
                        loading={isSubmitting}
                        className="btn btn-primary btn-success"
                      >
                        Send
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        ) : (
          <EmptyBar />
        )}
      </Card>
      <Card title="Recipients">
        <SortableTableWithPagination
          data={predefinedDataTable}
          tableColumns={getTableColumns([
            { key: 'seatsIoChannelName', label: 'Channel' },
            { key: 'email', label: 'Recipient Email' },
            { key: 'promoterEmail', label: 'Promoter Email', normalizer: value => value || 'No Promoter' },
            { key: 'statusText', label: 'Status' },
          ])}
          actions={[
            {
              label: 'Resend',
              className: 'btn btn-primary',
              icon: (
                <img
                  alt="nodata"
                  className="default-icosize"
                  src={asset('/resources/images/resend-ico.svg')}
                />
              ),
              isLoading: rowData => isLoadingResendInvitationId === rowData.id,
              disabled: rowData => rowData.status === "withdrawn",
              onClick: rowData => setShowModalResendInvitationId(rowData.id),
            },
            {
              label: 'Copy',
              className: 'btn btn-primary btn-secondary',
              icon: (
                <img
                  alt="copy"
                  className="default-icosize"
                  src={asset('/resources/images/content-duplicate.svg')}
                />
              ),
              isLoading: rowData => isFetchingInvitationUrl[rowData.id],
              onClick: rowData => copyInvitation(rowData),
            },
            {
              label: 'Withdraw',
              className: 'btn btn-primary btn-danger',
              disabled: rowData => rowData.status === "withdrawn",
              isLoading: rowData => isLoadingWithdrawInvitationId === rowData.id,
              onClick: rowData => setShowModalWithdrawInvitationId(rowData.id),
            },
          ]}
          disableMobileView={true}
          enableSort={true}
          detailsRows={detailRows}
          enableCopyTable={true}
          isAsyncDetails
          getDetailRowData={getDetailRowData}
          onPageOrLimitChange={onPageOrLimitChange}
          pageNumber={pagination.page}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          totalRecords={total}
          showPageLoading={true}
          isLoadingPageData={isLoadingPage}
        />
      </Card>
    </div>
  )
}

const PredefinedInvitationsRoot = props => {
  if (props.event.seatProvider === "seats_io") {
    return <PredefinedInvitationSeatsIo {...props} />
  }
  return <PredefinedInvitationsContainer {...props} />
}

const mapStateToProps = state => ({
  event: get_event(state),
})

const PredefinedInvitations = connect(mapStateToProps)(PredefinedInvitationsRoot)

export default PredefinedInvitations
